"use client";

import { hasCreatorInput } from "@/lib/influence/getInfluenceData";
import { useEffect, useRef, useState } from "react";
import InfluenceCreatorCard from "./InfluenceCreatorCard";
import WorkCard from "./WorkCard";
import { Work } from "./WorksSection";

export type InfluenceCreator = {
  id: string;
  avatar: string;
  name: string;
  role?: string;
  source?: string;
  sourceUrl?: string;
  episode?: string;
};

export type InfluenceSectionProps = {
  title: string;
  subtitle: string;
  type: "output" | "input";
  creators?: InfluenceCreator[];
  works?: Work[];
  className?: string;
  /** trueの場合、OUTPUTでも「作品」タブを非表示にし、1タブ表示にする */
  singleTab?: boolean;
  /** クリエイターの影響マップを表示するコールバック */
  onViewInfluence?: (_id: string) => void;
};

type OutputTab = "influence" | "works";
type InputCategory = "all" | "人物" | "映画" | "アニメ" | "漫画" | "小説";

const INPUT_CATEGORIES: { label: string; value: InputCategory }[] = [
  { label: "影響を受けたもの : all", value: "all" },
  { label: "映画", value: "映画" },
  { label: "アニメ", value: "アニメ" },
  { label: "漫画", value: "漫画" },
  { label: "小説", value: "小説" },
  { label: "人物", value: "人物" },
];

// UIラベル→works.jsonのcategory値マッピング（v2スキーマ: "漫画"）
const CATEGORY_DATA_MAP: Record<string, string> = {
  映画: "映画",
  アニメ: "アニメ",
  漫画: "漫画",
  小説: "小説",
};

export default function InfluenceSection({
  title,
  subtitle,
  type,
  creators = [],
  works = [],
  className = "",
  singleTab = false,
  onViewInfluence,
}: InfluenceSectionProps) {
  const [activeTab, setActiveTab] = useState<OutputTab>("works");
  const [inputCategory, setInputCategory] = useState<InputCategory>("all");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(true);
  const tabRowRef = useRef<HTMLDivElement>(null);
  const [showTabLeftFade, setShowTabLeftFade] = useState(false);
  const [showTabRightFade, setShowTabRightFade] = useState(true);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const hasDraggedRef = useRef(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    // 5px の余裕を持たせて最下部判定
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 5;
    setShowFade(!atBottom);
  };

  const handleTabRowScroll = () => {
    const el = tabRowRef.current;
    if (!el) return;
    setShowTabLeftFade(el.scrollLeft > 5);
    setShowTabRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  };

  const handleTabMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = tabRowRef.current;
    if (!el) return;
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartScrollRef.current = el.scrollLeft;
    el.style.cursor = "grabbing";
  };

  const handleTabMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    const el = tabRowRef.current;
    if (!el) return;
    const delta = e.clientX - dragStartXRef.current;
    if (Math.abs(delta) > 5) hasDraggedRef.current = true;
    el.scrollLeft = dragStartScrollRef.current - delta;
  };

  const handleTabMouseUp = () => {
    isDraggingRef.current = false;
    const el = tabRowRef.current;
    if (el) el.style.cursor = "";
  };

  const titleColor = type === "output" ? "text-[#f093fb]" : "text-[#4ecdc4]";
  const underlineColor = type === "output" ? "bg-[#f093fb]" : "bg-[#4ecdc4]";

  const showOutputTabs = type === "output" && !singleTab;
  const showInputCategoryFilter = type === "input" && (creators.length > 0 || works.length > 0);
  const showSingleOutputTab = type === "output" && singleTab;

  useEffect(() => {
    const el = tabRowRef.current;
    if (!el) return;
    const check = () => setShowTabRightFade(el.scrollWidth > el.clientWidth + 5);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showInputCategoryFilter]);

  // コンテンツ高さが変わったとき（カテゴリ切替など）にフェード表示を再評価する
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 5;
      setShowFade(!atBottom);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  });

  // Inputセクション用フィルター済みデータ
  const filteredInputCreators = inputCategory === "all" || inputCategory === "人物" ? creators : [];
  const filteredInputWorks = (() => {
    if (inputCategory === "all") return works;
    if (inputCategory === "人物") return [];
    return works.filter((w) => w.category === CATEGORY_DATA_MAP[inputCategory]);
  })();

  // アクティブタブに表示するコンテンツが存在するかどうか
  const hasContent = showOutputTabs
    ? activeTab === "influence"
      ? creators.length > 0
      : works.length > 0
    : type === "input"
      ? filteredInputCreators.length > 0 || filteredInputWorks.length > 0
      : creators.length > 0 || works.length > 0;

  // タブの色設定（Tailwindの静的解析に対応するため、完全なクラス名で記述）
  const tabActiveClass =
    type === "output"
      ? "border-[#f093fb]/60 bg-gradient-to-br from-[#f093fb]/20 to-[#f093fb]/10 text-[#f093fb] shadow-[0_4px_15px_rgba(240,147,251,0.3)]"
      : "border-[#4ecdc4]/60 bg-gradient-to-br from-[#4ecdc4]/20 to-[#4ecdc4]/10 text-[#4ecdc4] shadow-[0_4px_15px_rgba(78,205,196,0.3)]";
  const tabInactiveClass =
    type === "output"
      ? "border-[#f093fb]/30 bg-white/5 text-[#8b92a8] hover:border-[#f093fb]/50 hover:bg-white/10 hover:text-white"
      : "border-[#4ecdc4]/30 bg-white/5 text-[#8b92a8] hover:border-[#4ecdc4]/50 hover:bg-white/10 hover:text-white";

  return (
    <div className={`animate-fade-in-delay-long ${className}`}>
      <div className="relative mb-8 text-center">
        <p className={`${titleColor} text-base font-semibold`}>{title}</p>
        <p className={`${titleColor} my-1 text-sm`}>{subtitle}</p>
        <div
          className={`absolute bottom-[-16px] left-1/2 h-[3px] w-12 -translate-x-1/2 rounded ${underlineColor}`}
        />
      </div>

      {/* タブUI（OUTPUTセクション: 2タブ） */}
      {showOutputTabs && (
        <div className="mb-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={() => setActiveTab("works")}
            className={`cursor-pointer rounded-[25px] border px-4 py-1.5 text-xs font-medium backdrop-blur-[10px] transition-all duration-300 ${
              activeTab === "works" ? tabActiveClass : tabInactiveClass
            }`}
          >
            作品
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("influence")}
            className={`cursor-pointer rounded-[25px] border px-4 py-1.5 text-xs font-medium backdrop-blur-[10px] transition-all duration-300 ${
              activeTab === "influence" ? tabActiveClass : tabInactiveClass
            }`}
          >
            影響を与えた人や作品
          </button>
        </div>
      )}

      {/* タブUI（OUTPUTセクション: 1タブ - singleTab時） */}
      {showSingleOutputTab && (
        <div className="mb-6 flex justify-center gap-3">
          <button
            type="button"
            className={`cursor-pointer rounded-[25px] border px-4 py-1.5 text-xs font-medium backdrop-blur-[10px] transition-all duration-300 ${tabActiveClass}`}
          >
            影響を与えた人や作品
          </button>
        </div>
      )}

      {/* カテゴリフィルター（INPUTセクション） */}
      {showInputCategoryFilter && (
        <div className="relative mb-6">
          {/* 左グラデード（スクロール中のみ表示） */}
          <div
            className={`pointer-events-none absolute left-0 top-0 z-10 h-full w-8 bg-gradient-to-r from-[#0d0d1e] to-transparent transition-opacity duration-200 ${
              showTabLeftFade ? "opacity-100" : "opacity-0"
            }`}
          />
          {/* 右グラデード（右端に続きがある間表示） */}
          <div
            className={`pointer-events-none absolute right-0 top-0 z-10 h-full w-8 bg-gradient-to-l from-[#0d0d1e] to-transparent transition-opacity duration-200 ${
              showTabRightFade ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            ref={tabRowRef}
            onScroll={handleTabRowScroll}
            onMouseDown={handleTabMouseDown}
            onMouseMove={handleTabMouseMove}
            onMouseUp={handleTabMouseUp}
            onMouseLeave={handleTabMouseUp}
            className="flex cursor-grab select-none gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {INPUT_CATEGORIES.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  if (hasDraggedRef.current) return;
                  setInputCategory(value);
                }}
                className={`shrink-0 cursor-pointer rounded-[25px] border px-4 py-1.5 text-xs font-medium backdrop-blur-[10px] transition-all duration-300 ${
                  inputCategory === value ? tabActiveClass : tabInactiveClass
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative">
        <div
          key={type === "input" ? inputCategory : activeTab}
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex animate-tab-switch gap-5 overflow-x-auto pb-2.5 [scrollbar-width:none] lg:grid lg:h-[700px] lg:content-start lg:overflow-y-auto lg:overflow-x-hidden lg:pb-0 [&::-webkit-scrollbar]:hidden"
        >
          {/* タブ表示時: アクティブタブに応じて切り替え */}
          {showOutputTabs ? (
            <>
              {activeTab === "influence" &&
                creators.map((creator) => (
                  <InfluenceCreatorCard
                    key={creator.id}
                    avatar={creator.avatar}
                    name={creator.name}
                    role={creator.role}
                    type={type}
                    source={creator.source}
                    sourceUrl={creator.sourceUrl}
                    episode={creator.episode}
                    onViewInfluence={
                      onViewInfluence && hasCreatorInput(creator.id)
                        ? () => onViewInfluence(creator.id)
                        : undefined
                    }
                  />
                ))}
              {activeTab === "works" &&
                works.map((work) => (
                  <WorkCard
                    key={work.id}
                    thumbnail={work.thumbnail}
                    title={work.title}
                    year={work.year}
                    creators={work.creators}
                    category={work.category}
                    type={type}
                    source={work.source}
                    sourceUrl={work.sourceUrl}
                    episode={work.episode}
                  />
                ))}
            </>
          ) : (
            /* タブ非表示時: creators→worksの順で表示（Inputはフィルター済みデータを使用） */
            <>
              {(type === "input" ? filteredInputCreators : creators).map((creator) => (
                <InfluenceCreatorCard
                  key={creator.id}
                  avatar={creator.avatar}
                  name={creator.name}
                  role={creator.role}
                  type={type}
                  source={creator.source}
                  sourceUrl={creator.sourceUrl}
                  episode={creator.episode}
                  onViewInfluence={
                    onViewInfluence && hasCreatorInput(creator.id)
                      ? () => onViewInfluence(creator.id)
                      : undefined
                  }
                />
              ))}
              {(type === "input" ? filteredInputWorks : works).map((work) => (
                <WorkCard
                  key={work.id}
                  thumbnail={work.thumbnail}
                  title={work.title}
                  year={work.year}
                  creators={work.creators}
                  category={work.category}
                  type={type}
                  source={work.source}
                  sourceUrl={work.sourceUrl}
                  episode={work.episode}
                />
              ))}
            </>
          )}
        </div>

        {/* グラデーションフェードアウト（デスクトップのみ） */}
        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 hidden h-20 bg-gradient-to-t from-[#0d0d1e] to-transparent transition-opacity duration-300 lg:block ${
            hasContent && showFade ? "opacity-100" : "opacity-0"
          }`}
        />
      </div>
    </div>
  );
}
